/*
  kx-search-synonyms.js — write brand names the way customers type them.

  Shopify's search matches text, not intent, so a Bulgarian shopper typing "грое" or
  "мараци" sees a fraction of the catalogue: грое 9 results vs grohe 250, мараци 11 vs
  marazzi 190, "ханс грое" 0 vs hansgrohe 138. The store's product text is in Latin for
  the brand names, so the fix is to rewrite the QUERY before it is searched.

  Where it applies:
    - every search form (header + the results page) — the query is corrected on submit,
      so the page loads already corrected, with no redirect flash
    - a URL opened directly with an old query (history, bookmark, a link) — corrected by
      replacing the URL
    - the predictive dropdown, through window.kxSearchNormalize

  The page then says which term it searched, with a link to run the original untouched —
  the Google pattern. Nothing is silently swallowed.

  Only whole words are replaced, and only brand names, so "смесител грое" becomes
  "смесител grohe" and a query that merely contains a brand keeps its other words. This
  is deliberately not a spellchecker: a wrong guess here hides products.

  The same three places also join a GROHE article number typed with spaces
  ("24 172 KF1" -> 24172KF1) - see joinCodes below.

  A product code typed with the keyboard left on Bulgarian ("А8520АА" - the letters look the
  same, but they are Cyrillic) is searched in Latin letters when that spelling is the code of
  a product in the shop - see resolveLatin below.
*/
(function () {
  // multi-word first: "ханс грое" must not become "hansgrohe grohe" through the word map
  var PHRASES = [
    ['ханс грое', 'hansgrohe'],
    ['ханс грохе', 'hansgrohe'],
    ['идеал стандарт', 'ideal standard'],
    ['идеал стандард', 'ideal standard'],
    ['айдиъл стандарт', 'ideal standard']
  ];
  var WORDS = {
    // Ideal Standard: "standart" is the most common misspelling in both scripts
    standart: 'standard', стандарт: 'standard', стандард: 'standard', идеал: 'ideal',
    // the rest are simply the Cyrillic spellings of Latin brand names
    роца: 'roca', роса: 'roca',
    грое: 'grohe', грохе: 'grohe',
    хансгрое: 'hansgrohe', хансгрохе: 'hansgrohe',
    геберит: 'geberit',
    мараци: 'marazzi', марацци: 'marazzi',
    маинзу: 'mainzu', майнзу: 'mainzu',
    керабен: 'keraben',
    балдосер: 'baldocer', балдоцер: 'baldocer',
    алкапласт: 'alcadrain', alcaplast: 'alcadrain', алкадрейн: 'alcadrain',
    // not a brand, but the same problem (Clarity, 29 Oct 2026: shoppers looped on it): the tiles
    // carry it in Latin ("Mystone Trav.Classico", series Mystone Travertino), and measured on the
    // live search "травертин" found 1 unrelated tile while "travertin" leads with those
    травертин: 'travertin', травертина: 'travertin', травертини: 'travertin',
    травертинов: 'travertin', травертинова: 'travertin', травертинови: 'travertin'
  };

  /*
    Article numbers typed the way GROHE prints them. Brochures and boxes show "24 172 KF1"
    or "26 689 2431"; the shop stores 24172KF1, and Shopify matches whole words, so the
    spaced form found nothing (measured 2026-10-07: "24 172 KF1" 0 results, "24172KF1" 1).

    The shape is strict on purpose - two digits, three digits, then the finish code - so a
    size search such as "60 120 mat", "60 120 2cm" or "60 120 20mm" is left alone:
      3 characters: 000 / KF1 / P00 / 30E   (never one digit followed by two letters)
      4-5 characters: digits only (2431, 40000)
    A joined code that finds nothing is handed back to the shopper's own spacing further
    down, because a few sets carry the spaced code only in their name.
  */
  var CODE = /(^|[^0-9a-z])(\d{2}) (\d{3}) (\d{3,5}|[a-z]{2}\d|[a-z]\d{2}|\d{2}[a-z])(?![0-9a-z])/gi;
  function joinCodes(q) {
    return q ? q.replace(CODE, '$1$2$3$4') : q;
  }

  function brands(raw) {
    if (!raw) return raw;
    var q = String(raw).replace(/\s+/g, ' ').trim();
    var low = q.toLowerCase();
    for (var i = 0; i < PHRASES.length; i++) {
      if (low.indexOf(PHRASES[i][0]) !== -1) {
        low = low.split(PHRASES[i][0]).join(PHRASES[i][1]);
      }
    }
    var out = low.split(' ').map(function (w) {
      var bare = w.replace(/[„“"'.,!?]/g, '');
      return Object.prototype.hasOwnProperty.call(WORDS, bare) ? WORDS[bare] : w;
    }).join(' ');
    return out === low && low === q.toLowerCase() ? q : out;
  }

  /*
    A product code typed with the keyboard left on Bulgarian. Owner, 2026-10-09: typing A8520AA
    by hand found nothing, pasting it found the product - the typed letters were the Cyrillic
    "А", which looks the same and matches no SKU (measured: "А8520АА" 0 results, "A8520AA" 1).

    Such a word is rewritten to Latin ONLY when the Latin spelling is the code of a product in
    the shop - the shop is asked first (resolveLatin). Counting on "nothing found" was not
    enough: Shopify's typo tolerance answers "24172КФ1" with one unrelated thermostat and
    "Т4712АА" with fifteen basins, so those shoppers would never have been helped. And a word
    that is not a code of ours is left exactly as typed, so nothing can be hidden by a guess.
    A word is code-like when it mixes Cyrillic letters with three or more digits and is not a
    size or a quantity ("60х120", "80см", "12л").

    Which Latin letter a Cyrillic one stands for depends on how it was typed, so there can be
    up to three spellings, asked in this order:
      1. the same KEY on the traditional phonetic layout (в = W, ж = V, ь = X, я = Q)
      2. the same key on the newer phonetic layout       (в = V, ж = X, ч = Q, ш = W)
      3. the look-alike letter, for someone on the BDS layout picking letters by shape
         (Р = P, С = C, Н = H, В = B, Х = X, У = Y)
  */
  var KEY = { а: 'a', б: 'b', ц: 'c', д: 'd', е: 'e', ф: 'f', г: 'g', х: 'h', и: 'i', й: 'j', к: 'k', л: 'l',
              м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ъ: 'y', з: 'z' };
  var LAYOUTS = [
    { в: 'w', ж: 'v', ь: 'x', я: 'q' },
    { в: 'v', ж: 'x', ч: 'q', ш: 'w' },
    { а: 'a', в: 'b', е: 'e', к: 'k', м: 'm', н: 'h', о: 'o', р: 'p', с: 'c', т: 't', у: 'y', х: 'x' }
  ];
  var NOT_CODE = /^\d+([.,]\d+)?\s*[хx×*\/]\s*\d|^\d+([.,]\d+)?(см|мм|м|м2|л|бр|кг|вт|бара?)\.?$/i;
  function latinWord(w, layout, own) {
    var out = '';
    for (var i = 0; i < w.length; i++) {
      var ch = w.charAt(i), low = ch.toLowerCase();
      if (!/[а-яё]/.test(low)) { out += ch; continue; }
      var lat = Object.prototype.hasOwnProperty.call(layout, low) ? layout[low]
              : (!own && Object.prototype.hasOwnProperty.call(KEY, low)) ? KEY[low] : null;
      if (lat === null) return null;             // a letter this spelling cannot produce
      out += ch === low ? lat : lat.toUpperCase();
    }
    return out;
  }
  // every Latin spelling of the query: [{ q: the whole query respelled, codes: the respelled words }]
  function spellings(raw) {
    var q = String(raw || '').replace(/\s+/g, ' ').trim(), found = [];
    if (!q) return found;
    var words = q.split(' ');
    LAYOUTS.forEach(function (layout, n) {
      var codes = [];
      var out = words.map(function (w) {
        if (!/[а-яё]/i.test(w) || (w.match(/\d/g) || []).length < 3 || NOT_CODE.test(w)) return w;
        var lat = latinWord(w, layout, n === 2);  // look-alikes stand alone, keys fill in the rest
        if (lat === null) return w;
        codes.push(lat);
        return lat;
      }).join(' ');
      if (codes.length && out !== q && !found.some(function (f) { return f.q === out; })) found.push({ q: out, codes: codes });
    });
    return found;
  }
  function latinCodes(raw) {
    return spellings(raw).map(function (s) { return s.q; });
  }

  // Is this word the code of a product in the shop? The suggest endpoint, restricted to the SKU
  // field, matches a code or its beginning and applies no typo tolerance (measured: A8520AA 1,
  // A8520AB 0, W0074 1, V0074 0). Remembered for the visit, so a results page does not ask again
  // what the form asked a moment ago.
  var known = {};
  function isCode(word) {
    var key = 'kx-sku:' + word.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(known, key)) return Promise.resolve(known[key]);
    try {
      var kept = sessionStorage.getItem(key);
      if (kept !== null) return Promise.resolve(known[key] = kept === '1');
    } catch (e) {}
    if (typeof fetch !== 'function') return Promise.resolve(false);
    var base = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
    return fetch(base + '.json?q=' + encodeURIComponent(word) + '&resources[type]=product&resources[limit]=1' +
                 '&resources[options][fields]=variants.sku&resources[options][unavailable_products]=last')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        if (!j) return false;                     // the shop did not answer: do not remember it
        var yes = !!(j.resources && j.resources.results && j.resources.results.products && j.resources.results.products.length);
        known[key] = yes;
        try { sessionStorage.setItem(key, yes ? '1' : '0'); } catch (e) {}
        return yes;
      })
      .catch(function () { return false; });
  }
  // The query with its Cyrillic-typed codes in Latin, or null when no spelling is a code of ours.
  // Never waits longer than 2.5 s - a search must not hang on this.
  function resolveLatin(raw) {
    var list = spellings(raw);
    if (!list.length) return Promise.resolve(null);
    var answer = list.reduce(function (found, s) {
      return found.then(function (hit) {
        if (hit) return hit;
        return Promise.all(s.codes.map(isCode)).then(function (all) {
          return all.every(Boolean) ? s.q : null;
        });
      });
    }, Promise.resolve(null));
    var limit = new Promise(function (resolve) { setTimeout(function () { resolve(null); }, 2500); });
    return Promise.race([answer, limit]);
  }
  window.kxSearchLatinCodes = latinCodes;
  window.kxSearchLatin = resolveLatin;

  function normalize(raw) {
    return joinCodes(brands(raw));
  }

  window.kxSearchNormalize = normalize;
  // the brand fix alone, without joining codes - what the fallbacks search with
  window.kxSearchBrands = brands;

  // 1. correct on submit, so the corrected query is what loads
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.action || form.action.indexOf('/search') === -1) return;
    var input = form.querySelector('input[name="q"]');
    if (!input) return;
    var typed = input.value.trim();
    var fixed = normalize(typed) || typed;
    function hidden(name, value) {
      var el = document.createElement('input');
      el.type = 'hidden';
      el.name = name;
      el.value = value;
      form.appendChild(el);
    }
    function put(value, latin) {
      if (!value || value === typed) return;
      hidden('kx_from', typed);
      if (latin) hidden('kx_lat', '1');
      input.value = value;
    }
    // a code typed in Cyrillic: hold the form while the shop is asked which spelling it knows
    if (spellings(fixed).length) {
      e.preventDefault();
      resolveLatin(fixed).then(function (latin) {
        put(latin || fixed, !!latin);
        form.submit();                           // does not fire this listener again
      });
      return;
    }
    put(fixed, false);
  }, true);

  // 2. a URL that arrives with an old query still gets corrected
  var params = new URLSearchParams(location.search);
  var q = params.get('q');
  var leaving = false;                     // another page is loading: do not flash the note
  // kx_exact means the shopper asked for their own wording back — never re-correct it,
  // or the "search exactly" link would bounce straight back to the corrected query
  if (q && !params.get('kx_from') && !params.get('kx_exact')) {
    var fixed = normalize(q);
    if (fixed && fixed !== q.replace(/\s+/g, ' ').trim()) {
      params.set('q', fixed);
      params.set('kx_from', q);
      location.replace(location.pathname + '?' + params.toString());
      return;
    }
  }
  // ... and so does one that holds a code typed in Cyrillic (a link, the back button, a form
  // that was sent without this script)
  if (q && !params.get('kx_exact') && !params.get('kx_lat') && spellings(q).length) {
    resolveLatin(q).then(function (latin) {
      if (!latin) return;
      var to = new URLSearchParams(location.search);
      to.set('q', latin);
      if (!to.get('kx_from')) to.set('kx_from', q);
      to.set('kx_lat', '1');
      leaving = true;
      location.replace(location.pathname + '?' + to.toString());
    });
  }

  // 3. tell the shopper what was searched, and offer the original
  var from = params.get('kx_from');
  if (from && q) {
    document.addEventListener('DOMContentLoaded', function () {
      // A joined code that found nothing: search the shopper's own spacing instead, once.
      // "40 511 001" is such a case - no product has that number, but a set names it.
      // The same way back when a Latin respelling (kx_lat) found nothing after all.
      var meta = document.getElementById('kx-search-meta');
      var plain = brands(from);
      if (meta && meta.getAttribute('data-count') === '0' && !params.get('kx_exact') && plain &&
          (plain !== q || params.get('kx_lat'))) {
        var back = new URLSearchParams(location.search);
        back.set('q', plain);
        back.set('kx_exact', '1');
        back.delete('kx_lat');
        if (plain === from.replace(/\s+/g, ' ').trim()) back.delete('kx_from');
        leaving = true;
        location.replace(location.pathname + '?' + back.toString());
        return;
      }
      var slot = document.getElementById('kx-search-note');
      if (!slot || leaving) return;
      var exact = new URLSearchParams(location.search);
      exact.set('q', from);
      exact.delete('kx_from');
      exact.delete('kx_lat');
      exact.set('kx_exact', '1');
      slot.innerHTML = 'Показваме резултати за <b></b>. ' +
        '<a href="' + location.pathname + '?' + exact.toString() + '">Търси точно „<span></span>“</a>';
      slot.querySelector('b').textContent = '„' + q + '“';
      slot.querySelector('span').textContent = from;
      slot.hidden = false;
    });
  }
})();
